import React, { startTransition, useState, Suspense } from 'react';
import { useQueryClient } from 'react-query';
import cx from 'classnames';
import { PaymentElement } from '@stripe/react-stripe-js';

import Modal from '~/components/modals/modal';
import PaymentForm from '~/components/checkout/payment-form';
import AuthFailModalCartSummary from '~/components/modals/account-settings/auth-fail-modal-cart-summary';
import { PaymentMethodSelection } from '~/components/checkout/checkout-form';
import { CreditCardBanner } from '~/components/modals/account-settings/update-payment-modal';
import { getDefaultCard } from '~/features/cart/cart';
import { captureException } from '~/utils/exception-tracking';
import { useFlashMessage } from '~/features/flash-messages/flash-message-actions';
import { usePaymentIntents } from '~/features/payment-methods/payment-method-actions';
import { useConfirmCartByUuid } from '~/hooks/use-cart';
import {
  useChildCarts,
  useProcessAuthFailOrder,
} from '~/features/cart/cart-actions';
import { accountSettingsRoute } from '~/routes';
import { useSession } from '~/features/session/session-actions';
import LoadingIndicator from '~/components/loading-indicator';
import AlertMessage from '~/components/messages/alert-message';
import TermsConsent from '~/components/cart/terms-consent';
import { toMonthAndYear } from '~/utils/dates';
import { centsToCurrency } from '~/utils/numbers';
import Button from '~/components/button';
import { safeLazy } from '~/utils/safe-lazy';

import modalStyles from '~/components/modals/modal.module.scss';
import styles from '~/components/modals/account-settings/account-settings-modals.module.scss';

const StripeProvider = safeLazy(
  () => import('~/components/form/stripe-provider')
);

const pages = {
  UPDATE_PAYMENT: 'UPDATE_PAYMENT',
  CONFIRM_CHARGE: 'CONFIRM_CHARGE',
};

const AuthFailModal = ({
  onClose = () => {},
  isOpen,
  nextCommittedOrderUuid,
  coupon,
  nextChargeDate,
  lawn,
}) => {
  const { user } = useSession();
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [page, setPage] = useState(pages.CONFIRM_CHARGE);

  if (!user) {
    return null;
  }

  if (isOpen && !nextCommittedOrderUuid) {
    captureException(
      new AuthFailError('missing next committed order uuid'),
      {}
    );

    return (
      <Modal
        onClose={onClose}
        allowClose={!isPurchasing}
        isOpen={isOpen}
        className={cx(styles.authFailModal, {
          [styles.confirmChargePage]: page === pages.UPDATE_PAYMENT,
        })}
      >
        <div className={styles.authFailModalNoCart}>
          <h1 className={modalStyles.title}>Oops.</h1>
          <p>There was an unexpected problem.</p>
          <p>Please try again.</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      onClose={onClose}
      allowClose={!isPurchasing}
      isOpen={isOpen}
      className={cx(styles.authFailModal, {
        [styles.confirmChargePage]: page === pages.UPDATE_PAYMENT,
      })}
    >
      <AuthFailModalContent
        nextCommittedOrderUuid={nextCommittedOrderUuid}
        setIsPurchasing={setIsPurchasing}
        isPurchasing={isPurchasing}
        coupon={coupon}
        nextChargeDate={nextChargeDate}
        page={page}
        setPage={setPage}
        onClose={onClose}
        lawn={lawn}
        user={user}
      />
    </Modal>
  );
};

export default AuthFailModal;

const AuthFailModalContent = ({
  nextCommittedOrderUuid,
  setIsPurchasing,
  isPurchasing,
  coupon,
  nextChargeDate,
  page,
  setPage,
  onClose,
  lawn,
  user,
}) => {
  const { showFlashMessage } = useFlashMessage();
  const queryClient = useQueryClient();
  const { processAuthFailOrder } = useProcessAuthFailOrder();
  const [isPaymentElementLoaded, setIsPaymentElementLoaded] = useState(false);

  const {
    cart,
    error: confirmedCartError,
    isLoading: isLoadingConfirmedCart,
  } = useConfirmCartByUuid({
    cartUuid: nextCommittedOrderUuid,
  });

  const {
    childCarts,
    isLoading: isLoadingChildCarts,
    error: childCartError,
  } = useChildCarts(cart);

  if (
    !isLoadingConfirmedCart &&
    !isLoadingChildCarts &&
    !cart?.isLegacyFlexPay &&
    !childCarts
  ) {
    captureException(
      new AuthFailError('missing childCarts for pay in full plan'),
      {
        extras: {
          cartUuid: cart?.uuid,
        },
      }
    );
  }

  const cartUuid = cart?.isLegacyFlexPay
    ? cart?.uuid
    : cart?.masterPurchaseOrder?.uuid;

  const {
    paymentIntent,
    isLoading: isLoadingPaymentIntent,
    error: paymentIntentError,
  } = usePaymentIntents(cartUuid, null, false);

  const [isUpdatingCoupon, setIsUpdatingCoupon] = useState(false);

  const onCouponChange = async () => {
    setIsUpdatingCoupon(true);
    await queryClient.refetchQueries('confirmedCart');
    await queryClient.refetchQueries(['paymentIntents', cartUuid]);
    setIsUpdatingCoupon(false);
  };

  const handleSubmit = async (paymentMethodId) => {
    try {
      setIsPurchasing(true);
      await processAuthFailOrder(cartUuid, paymentMethodId, lawn?.uuid);

      showFlashMessage(
        'Your payment method has been updated. Orders may take 1-2 days to process.',
        'success',
        5000
      );

      onClose();
    } catch (error) {
      captureException(new AuthFailError('handleSubmit'), {
        extras: {
          error,
          errorString: JSON.stringify(error),
          cartUuid: cart.uuid,
        },
      });

      showFlashMessage(
        'Oops, while your payment was successful, there was an issue updating your order.'
      );
      onClose();
    } finally {
      setIsPurchasing(false);
    }
  };

  let cartToUse = cart;
  if (!cart?.isLegacyFlexPay && childCarts) {
    const childCartContents = childCarts
      .map((childCart) => {
        return childCart?.contents;
      })
      .reduce((array1, array2) => array1.concat(array2));

    cartToUse = {
      ...cart,
      contents: cart?.isLegacyFlexPay
        ? [...cart.contents, ...childCartContents]
        : [...childCartContents],
    };
  }

  const redirectUrl = window.location.origin + accountSettingsRoute();

  const cardOnFile = getDefaultCard(cart);

  const nextShipmentDate = toMonthAndYear(nextChargeDate);

  const termsText = cart?.isLegacyFlexPay
    ? `Your next lawn plan shipment will be charged to your saved payment method in ${nextShipmentDate}. You will be charged before each shipment.`
    : `Your next full season lawn plan will be charged to your saved payment method in ${nextShipmentDate} and annually thereafter.`;

  const isLoading =
    isLoadingPaymentIntent || isLoadingConfirmedCart || isLoadingChildCarts;

  const error = paymentIntentError || confirmedCartError || childCartError;

  if (error) {
    captureError(error, cart);
  }

  const totalPriceWithTax = cart?.isLegacyFlexPay
    ? cart?.totalPriceWithTax
    : cart?.masterPurchaseOrder?.totalPriceWithTax;

  if (!isLoadingConfirmedCart && !totalPriceWithTax) {
    captureException(new AuthFailError('null or 0 totalPriceWithTax'), {
      extras: {
        cartUuid: cart?.uuid,
        totalPriceWithTax,
      },
    });
  }

  if (!isLoadingConfirmedCart && !isLoadingPaymentIntent && !paymentIntent) {
    captureException(new AuthFailError('paymentIntent is undefined'), {
      extras: {
        cartUuid: cart?.uuid,
      },
    });
  }

  if (
    !isLoadingConfirmedCart &&
    !isLoadingPaymentIntent &&
    !paymentIntent?.intent_type
  ) {
    captureException(
      new AuthFailError('Missing intent_type on paymentIntent'),
      {
        extras: {
          cartUuid: cart?.uuid,
        },
      }
    );
  }

  const canViewPage = cart && !isLoading && !error;

  return (
    <>
      {isLoading && (
        <div
          className={modalStyles.loadingIndicator}
          data-testid="loading-indicator"
        >
          <h1>Loading account info</h1>
          <LoadingIndicator />
        </div>
      )}
      {!cart && !isLoading && (
        <div className={styles.authFailModalNoCart}>
          <h1 className={modalStyles.title}>Oops.</h1>
          <p className={styles.authFailModalNoCartMessage}>
            It looks like you don't have anything to update at this time! Please
            contact our support team if you have any questions.
          </p>
          <Button fullWidth onClick={() => onClose()}>
            Close
          </Button>
        </div>
      )}
      {(error ||
        !paymentIntent ||
        !paymentIntent?.intent_type ||
        !lawn ||
        totalPriceWithTax === null ||
        totalPriceWithTax === undefined) &&
        !isLoading && (
          <div className={modalStyles.errorMessage} data-testid="error-message">
            <AlertMessage heading="Something went wrong">
              There was an unexpected problem, please try again later
            </AlertMessage>
          </div>
        )}

      {page === pages.CONFIRM_CHARGE && canViewPage && (
        <>
          <h1
            className={cx(modalStyles.title, styles.authFailModalTitle, {
              [styles.flexPayTitle]: cart?.isLegacyFlexPay,
            })}
          >
            What you're <br />
            getting in{' '}
            {cart?.isLegacyFlexPay ? 'your next order' : lawn?.currentPlanYear}
          </h1>
          <div className={styles.miniCartContainer}>
            <AuthFailModalCartSummary
              cart={cartToUse}
              coupon={coupon}
              onCouponAdd={onCouponChange}
              onCouponRemove={onCouponChange}
            />
          </div>
          <Button
            fullWidth
            isLoading={isUpdatingCoupon}
            onClick={() => {
              // Use startTransition now that we're lazy loading the stripe form
              startTransition(() => {
                setPage(pages.UPDATE_PAYMENT);
              });
            }}
          >
            Next: update payment
          </Button>
        </>
      )}
      {page === pages.UPDATE_PAYMENT && canViewPage && (
        <>
          <h1 className={cx(modalStyles.title, styles.authFailModalTitle)}>
            Payment info
          </h1>

          <p className={styles.authFailCopy}>
            Please update your payment information.
          </p>

          {cardOnFile && !isPurchasing && (
            <CreditCardBanner cardInfo={cardOnFile} variant="error" />
          )}
          <div className={styles.authFailFormContainer}>
            <Suspense fallback={null}>
              <StripeProvider
                amount={totalPriceWithTax}
                mode={paymentIntent.intent_type}
              >
                <PaymentForm
                  onSubmit={handleSubmit}
                  savedPaymentMethod={null}
                  initialValues={{
                    paymentMethod: PaymentMethodSelection.NEW,
                  }}
                  paymentIntent={paymentIntent}
                  cartUuid={cart.uuid}
                  redirectUrl={redirectUrl}
                >
                  {({ isSubmitting }) => (
                    <div
                      data-testid="stripe-payment-element-wrapper"
                      className={modalStyles.paymentWrapper}
                    >
                      <div className={styles.paymentAndSummary}>
                        <PaymentElement
                          className={modalStyles.paymentElement}
                          onReady={() => {
                            setIsPaymentElementLoaded(true);
                          }}
                          options={{
                            layout: { type: 'tabs' },
                            defaultValues: {
                              billingDetails: {
                                email: user?.email,
                              },
                            },
                            fields: {
                              billingDetails: {
                                address: {
                                  country: 'never',
                                },
                              },
                            },
                          }}
                        />

                        <div className={styles.cartSummaryWrapper}>
                          <h3 className={styles.subheading}>Summary</h3>
                          <div
                            className={['panel', styles.cartSummaryPanel].join(
                              ' '
                            )}
                          >
                            <AuthFailModalCartSummary
                              cart={cart}
                              showCouponLine={false}
                            />
                          </div>
                        </div>
                      </div>

                      <Button
                        className={styles.confirmButton}
                        type="submit"
                        isLoading={isSubmitting}
                        fullWidth
                        disabled={!isPaymentElementLoaded}
                      >
                        <img
                          className={styles.lockIcon}
                          src="/icons/lock-icon-white.svg"
                          alt="closed lock"
                        />
                        Confirm
                      </Button>
                    </div>
                  )}
                </PaymentForm>
              </StripeProvider>
            </Suspense>
            <div className={styles.termsContainer}>
              <p>
                By clicking "Confirm", your card will be charged immediately for
                the total amount of{' '}
                <span data-testid="total-in-terms-copy">
                  {centsToCurrency(totalPriceWithTax)}
                </span>
                .
              </p>
              <TermsConsent textBlocks={[termsText]} buttonText="Confirm" />
            </div>
          </div>
        </>
      )}
    </>
  );
};

class AuthFailError extends Error {
  constructor(message, error) {
    super(message);
    this.name = 'AuthFailError';
  }
}

const captureError = (error, cart) => {
  if (
    typeof error.errors === 'string' &&
    error.errors.includes('out of stock')
  ) {
    captureException(new AuthFailError('item out of stock'), {
      extras: {
        errorString: JSON.stringify(error),
        cartUuid: cart?.uuid,
        productOOS: error.errors.split('are out of stock')[0].trim(),
      },
    });
  } else {
    captureException(new AuthFailError('error displaying modal'), {
      extras: {
        errorString: JSON.stringify(error),
        cartUuid: cart?.uuid,
      },
    });
  }
};
